/**
 * Huayi Tracker SDK v1.1
 * 埋点采集：图片曝光 / 点击、按钮点击、活动入口曝光/点击
 *
 * 使用方式:
 *   <img data-track-id="home_hero_banner" />
 *   <button data-track-id="buy_now">Buy Now</button>
 *   <a data-track-id="summer_sale" href="/collections/summer">Summer Sale</a>
 *
 *   显式声明类型（可选）:
 *   <img data-track-id="x" data-track-type="image" />
 *   <button data-track-id="y" data-track-type="button" />
 *
 * 配置:
 *   window.Tracker.config({ endpoint: '/api/tracker/events', flushInterval: 500 });
 *   window.Tracker.debug(true);
 */
(function () {
  'use strict';

  // ══════════════════════════════════════════════════════════════
  //  内部状态
  // ══════════════════════════════════════════════════════════════
  var _config = {
    endpoint: 'https://shopify_lottery.vayi.cn/apps/lottery/tracker/events',
    flushInterval: 500,
    maxBatch: 20,
    site: 'lovevook',
    debug: false
  };

  var _eventQueue = [];
  var _flushTimer = null;
  var _sentViews = {};        // "trackId::contentKey" → true, 曝光去重
  var _sessionId = '';
  var _observer = null;
  var _isInitialized = false;
  var _deviceModelCache = '';   // UA-CH 异步获取的设备型号缓存
  var _debugEl = null;
  var _debugTimer = null;
  var _observedSet = [];       // 已观察元素的弱引用标记

  // ══════════════════════════════════════════════════════════════
  //  工具函数
  // ══════════════════════════════════════════════════════════════

  function hash(str) {
    var h = 5381, i = 0;
    for (; i < str.length; i++) { h = ((h << 5) + h + str.charCodeAt(i)) | 0; }
    return Math.abs(h).toString(36);
  }

  /** 提取 Shopify 图片文件名，忽略 CDN 域名、目录和动态参数 */
  function getShopifyImageFilename(url) {
    if (!url) return '';
    try {
      var u = url.indexOf('http') === 0 ? new URL(url) : new URL(url, location.origin);
      return decodeURIComponent(u.pathname.split('/').pop() || '').trim();
    } catch (e) {
      return String(url).split(/[?#]/)[0].split('/').pop().trim();
    }
  }

  /** 获取 pathname，保留业务参数，过滤追踪参数 */
  function getCleanPathname() {
    var raw = location.search;
    if (!raw) return location.pathname;
    var strip = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
      'fbclid', 'gclid', 'msclkid', '_ga', '_gl', 'mc_cid', 'mc_eid', '_hsenc', '_hsmi'];
    var sp = new URLSearchParams(raw);
    strip.forEach(function (k) { sp.delete(k); });
    var q = sp.toString();
    return location.pathname + (q ? '?' + q : '');
  }

  function detectDevice() {
    var ua = navigator.userAgent;
    // 平板优先判断（iPad 或 Android 平板，User Agent 不含 Mobile）
    if (/iPad|Android(?!.*Mobile)/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return 'tablet';
    }
    // 手机
    if (/Mobi|Android|iPhone/i.test(ua)) {
      return 'mobile';
    }
    return 'desktop';
  }

  /** 检测操作系统 */
  function detectOS() {
    var ua = navigator.userAgent;
    if (/Windows/i.test(ua)) return 'Windows';
    // iPhone/iPad 必须优先于 macOS 判断（iPhone UA 也含 "Mac OS"）
    if (/iPhone|iPad|iPod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'iOS';
    if (/Mac OS|Macintosh/i.test(ua)) return 'macOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/Linux/i.test(ua)) return 'Linux';
    return 'Other';
  }

  /**
   * 异步获取设备型号（UA-CH 优先，Chrome/Edge 等支持）
   * 成功后缓存到 _deviceModelCache，detectDeviceModel 优先返回缓存值
   */
  function fetchDeviceModel() {
    // 仅 Chrome/Edge 系支持 NavigatorUAData
    if (!navigator.userAgentData || !navigator.userAgentData.getHighEntropyValues) {
      _log('[Tracker] UA-CH 不可用，使用降级方案获取型号');
      return;
    }
    navigator.userAgentData.getHighEntropyValues(['model', 'platformVersion'])
      .then(function (hints) {
        if (hints.model && hints.model.trim()) {
          _deviceModelCache = hints.model.trim();
          _log('[Tracker] UA-CH 获取型号成功: ' + _deviceModelCache);
        }
      })
      .catch(function () {
        _log('[Tracker] UA-CH 获取型号失败，使用降级方案');
      });
  }

  /** 检测设备机型（UA-CH 缓存 → 分辨率/UA 正则降级） */
  function detectDeviceModel() {
    // 优先返回 UA-CH 缓存
    if (_deviceModelCache) return _deviceModelCache;

    var ua = navigator.userAgent;
    // Android: 提取 Build Model（如 SM-S928B）
    var m = ua.match(/; ([A-Za-z0-9 _-]+?) Build\//);
    if (m) return m[1].trim();
    // iOS: iPhone/iPad 型号不在 UA 里，通过屏幕分辨率粗略推断
    if (/iPhone/.test(ua)) {
      var w = Math.max(screen.width, screen.height);
      var h = Math.min(screen.width, screen.height);
      var dpr = window.devicePixelRatio || 1;
      var pw = Math.round(w * dpr);  // 较长边物理像素（竖屏高度）
      var ph = Math.round(h * dpr);  // 较短边物理像素（竖屏宽度）
      // 常见 iPhone 分辨率映射（pw = 高, ph = 宽）
      if (pw === 2796 && ph === 1290) return 'iPhone 15 Pro Max / 16 Pro Max';
      if (pw === 2556 && ph === 1179) return 'iPhone 15 / 16 Pro';
      if (pw === 2532 && ph === 1170) return 'iPhone 14 / 15 / 16';
      if (pw === 2778 && ph === 1284) return 'iPhone 12/13 Pro Max / 14 Plus';
      if (pw === 2436 && ph === 1125) return 'iPhone X / XS / 11 Pro';
      if (pw === 1792 && ph === 828) return 'iPhone XR / 11';
      if (pw === 1334 && ph === 750) return 'iPhone 6/7/8 / SE';
      return 'iPhone (' + pw + 'x' + ph + ')';
    }
    if (/iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return 'iPad';
    }
    return '';
  }

  /** 检测设备品牌（结合 UA-CH 缓存 + UA 解析） */
  function detectDeviceBrand() {
    var ua = navigator.userAgent;

    // iPhone / iPad → Apple
    if (/iPhone|iPad|iPod/i.test(ua)) return 'Apple';
    if (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) return 'Apple';

    // 优先从 UA-CH 缓存型号推断
    if (_deviceModelCache) {
      var m = _deviceModelCache;
      if (/^(SM-|GT-|SGH-|SCH-|SHV-|SHW-|SP-)/i.test(m)) return 'Samsung';
      if (/^(Pixel|Nexus)/i.test(m)) return 'Google';
      if (/^(Redmi|Mi |POCO|M2[0-9]|2[0-9]0[0-9])/i.test(m)) return 'Xiaomi';
      if (/^(HUAWEI|HONOR|ALN|ANA|BNE|CLT|ELE|ELS|EVR|JNY|LIO|NOH|OCE|PCT|TAS|VOG|YAL)/i.test(m)) return 'Huawei';
      if (/^(OPPO|CPH[0-9]|PCH|PEH|PGH|PHW|RMX)/i.test(m)) return 'OPPO';
      if (/^(vivo|V[0-9]{4}|I[0-9]{4}|Y[0-9]{2,3})/i.test(m)) return 'vivo';
      if (/^(OnePlus|ONEPLUS|LE2[0-9]|KB2[0-9])/i.test(m)) return 'OnePlus';
      if (/^(moto|XT[0-9])/i.test(m)) return 'Motorola';
      if (/^(LG-|LM-)/i.test(m)) return 'LG';
    }

    // UA 降级匹配
    if (/Samsung|SM-/i.test(ua)) return 'Samsung';
    if (/Xiaomi|Redmi/i.test(ua)) return 'Xiaomi';
    if (/Huawei|Honor|HarmonyOS/i.test(ua)) return 'Huawei';
    if (/OPPO/i.test(ua)) return 'OPPO';
    if (/vivo/i.test(ua)) return 'vivo';
    if (/OnePlus/i.test(ua)) return 'OnePlus';
    if (/Motorola|Moto/i.test(ua)) return 'Motorola';
    if (/LG[ -]/i.test(ua)) return 'LG';
    if (/Sony/i.test(ua)) return 'Sony';
    if (/Nokia/i.test(ua)) return 'Nokia';
    if (/Google|Pixel/i.test(ua)) return 'Google';
    if (/Android/i.test(ua)) return 'Other';

    return '';
  }

  function detectBrowser() {
    var ua = navigator.userAgent;
    if (ua.indexOf('Edg') > -1) return 'edge';
    if (ua.indexOf('Chrome') > -1) return 'chrome';
    if (ua.indexOf('Safari') > -1) return 'safari';
    if (ua.indexOf('Firefox') > -1) return 'firefox';
    return 'other';
  }

  function getSessionId() {
    if (_sessionId) return _sessionId;
    try {
      _sessionId = sessionStorage.getItem('_ht_sid');
      if (!_sessionId) {
        _sessionId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
        sessionStorage.setItem('_ht_sid', _sessionId);
      }
    } catch (e) {
      _sessionId = Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
    }
    return _sessionId;
  }

  /** 访客 ID — localStorage 持久化，关浏览器也不变 */
  function getVisitorId() {
    try {
      var vid = localStorage.getItem('_ht_vid');
      if (!vid) {
        vid = 'v_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
        localStorage.setItem('_ht_vid', vid);
      }
      return vid;
    } catch (e) { return ''; }
  }

  /** 推断元素类型：优先 data-track-type 属性，其次标签名 */
  function detectElementType(el) {
    var explicit = el.getAttribute('data-track-type');
    if (explicit) return explicit;

    var tag = el.tagName.toLowerCase();
    if (tag === 'img' || tag === 'picture' || tag === 'video') return 'image';
    if (tag === 'button' || (tag === 'input' && (el.type === 'button' || el.type === 'submit'))) return 'button';
    if (el.getAttribute('role') === 'button') return 'button';
    if (tag === 'a') {
      // a 标签有 href 且指向站内其他页面 → activity，否则 button
      try {
        var href = el.getAttribute('href') || '';
        if (href && href !== '#' && href.indexOf('javascript:') !== 0) {
          var u = new URL(href, location.origin);
          if (u.origin === location.origin && u.pathname !== location.pathname) return 'activity';
        }
      } catch (e) {}
      return 'button';
    }
    // div/span 等容器：检查是否包裹了 img
    if (el.querySelector('img')) return 'image';
    return 'button'; // 默认按按钮处理
  }

  function extractMeta(el, type) {
    var meta = { title: '', text: '', href: '', alt: '' };

    meta.title = el.getAttribute('aria-label') || el.getAttribute('title') || '';

    if (type === 'image') {
      meta.alt = el.getAttribute('alt') || '';
    }

    if (type === 'button' || type === 'activity') {
      meta.text = (el.innerText || el.textContent || '').trim().slice(0, 100);
    }

    if (type === 'activity') {
      // 卡片容器通常包裹一个覆盖层链接，点击时 el 可能是容器本身。
      var a = el.tagName === 'A' ? el : (el.closest('a') || el.querySelector('a'));
      if (a) meta.href = a.getAttribute('href') || '';
    }

    // 对于 image 的容器（如 div[data-track-id] 包裹 img）
    if (!meta.title && type === 'image' && !meta.alt) {
      var innerImg = el.tagName === 'IMG' ? el : el.querySelector('img');
      if (innerImg) meta.alt = innerImg.getAttribute('alt') || '';
    }

    if (!meta.title && meta.text) meta.title = meta.text;
    if (!meta.title && type === 'image') meta.title = meta.alt;

    return meta;
  }

  function computeContentKey(el) {
    // 优先从 img 获取 src（即使是容器元素）
    var imgEl = el.tagName === 'IMG' ? el : el.querySelector('img');
    var trackId = (el.getAttribute('data-track-id') || '').trim();
    if (imgEl && imgEl.src) {
      var filename = getShopifyImageFilename(imgEl.src);
      if (filename) return hash(trackId + '|' + filename);
    }

    // 非图片元素继续使用文案生成内容版本
    var parts = [
      (el.getAttribute('alt') || (imgEl ? imgEl.getAttribute('alt') : '') || '').trim(),
      trackId,
      (el.innerText || el.textContent || '').trim().slice(0, 100)
    ];
    return hash(parts.join('|'));
  }

  // ══════════════════════════════════════════════════════════════
  //  事件采集
  // ══════════════════════════════════════════════════════════════

  function buildEvent(eventType, el) {
    var type = detectElementType(el);
    var meta = extractMeta(el, type);

    // 提取图片 src（用于后台预览）
    var src = '';
    if (el.tagName === 'IMG') {
      src = el.src || '';
    } else {
      var innerImg = el.querySelector('img');
      if (innerImg) src = innerImg.src || '';
    }

    return {
      event: eventType,
      trackId: el.getAttribute('data-track-id'),
      contentKey: computeContentKey(el),
      elementType: type,
      pathname: getCleanPathname(),
      title: meta.title,
      text: meta.text,
      href: meta.href,
      alt: meta.alt,
      src: src,
      timestamp: Math.floor(Date.now() / 1000),
      sessionId: getSessionId(),
      visitorId: getVisitorId(),
      device: detectDevice(),
      browser: detectBrowser(),
      os: detectOS(),
      deviceModel: detectDeviceModel(),
      deviceBrand: detectDeviceBrand(),
      country: '',
      extra: location.search ? { query: location.search } : {}
    };
  }

  function trackView(el) {
    var trackId = el.getAttribute('data-track-id');
    var ck = computeContentKey(el);
    var dedupKey = trackId + '::' + ck;
    if (_sentViews[dedupKey]) return;
    _sentViews[dedupKey] = true;

    var ev = buildEvent('view', el);
    enqueue(ev);
    _debugLog('VIEW', ev.trackId + ' (' + (ev.alt || ev.title || '') + ')');
    _log('[Tracker] VIEW  ' + ev.trackId);
  }

  function trackClick(el) {
    var ev = buildEvent('click', el);
    // 点击事件立即发送，不批量 — 防止页面跳转导致数据丢失
    send([ev]);
    _debugLog('CLICK', ev.trackId + ' (' + (ev.text || ev.title || '') + ')');
    _log('[Tracker] CLICK ' + ev.trackId + ' (即时发送)');
  }

  // ══════════════════════════════════════════════════════════════
  //  Event Queue + Batch
  // ══════════════════════════════════════════════════════════════

  function enqueue(ev) {
    _eventQueue.push(ev);
    if (_eventQueue.length >= _config.maxBatch) flush();
    else scheduleFlush();
  }

  function scheduleFlush() {
    if (_flushTimer) return;
    _flushTimer = setTimeout(flush, _config.flushInterval);
  }

  function flush() {
    clearTimeout(_flushTimer);
    _flushTimer = null;
    if (!_eventQueue.length) return;

    var batch = _eventQueue.splice(0, _config.maxBatch);
    send(batch);

    if (_eventQueue.length) {
      _flushTimer = setTimeout(flush, 50);
    }
  }

  function send(events) {
    var payload = JSON.stringify({ site: _config.site, events: events });
    _log('[Tracker] 发送 ' + events.length + ' 条事件 → ' + _config.endpoint);

    // 优先 sendBeacon — 页面跳转/关闭时最可靠，不阻塞页面
    if (navigator.sendBeacon) {
      var blob = new Blob([payload], { type: 'application/json' });
      var ok = navigator.sendBeacon(_config.endpoint, blob);
      if (ok) return;
      _log('[Tracker] sendBeacon 失败，降级 fetch keepalive');
    }

    // 降级：fetch + keepalive（现代浏览器都支持）
    fetch(_config.endpoint, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true
    }).catch(function () {});
  }

  // ══════════════════════════════════════════════════════════════
  //  监听器
  // ══════════════════════════════════════════════════════════════

  function setupViewTracking() {
    if (!window.IntersectionObserver) {
      _log('[Tracker] IntersectionObserver 不可用，跳过曝光监听');
      return;
    }

    _observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.1) return;

        var el = entry.target;
        // 找到元素内的图片（或自身就是 img）
        var img = el.tagName === 'IMG' ? el : el.querySelector('img');

        // 无图片（按钮/活动入口）→ 直接上报
        if (!img) {
          trackView(el);
          return;
        }

        // 图片已加载完 → 直接上报
        if (img.complete) {
          // naturalWidth > 0 说明加载成功，= 0 说明加载失败（也算曝光）
          trackView(el);
          return;
        }

        // 图片正在加载中 → 等 load/error 事件
        _log('[Tracker] 等待图片加载: ' + (el.getAttribute('data-track-id') || '') + ' src=' + img.src.slice(-40));
        var tracked = false;
        var onLoad = function () {
          if (tracked) return;
          tracked = true;
          img.removeEventListener('load', onLoad);
          img.removeEventListener('error', onLoad);
          // 再次确认仍在视口内
          var rect = el.getBoundingClientRect();
          if (rect.bottom > 0 && rect.top < window.innerHeight) {
            trackView(el);
          }
        };
        img.addEventListener('load', onLoad);
        img.addEventListener('error', onLoad); // 加载失败也上报（统计 broken image 曝光）
      });
    }, { threshold: [0, 0.1, 0.5] });

    scanElements();
    _log('[Tracker] IntersectionObserver 已启动 (threshold: 0.1)');
  }

  /** 扫描并观察带 data-track-id 的元素 */
  function scanElements() {
    var els = document.querySelectorAll('[data-track-id]');
    var newCount = 0;

    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      if (el.__ht_observed) continue;
      el.__ht_observed = true;

      var type = detectElementType(el);

      // 图片、banner、activity 类型听曝光
      if (_observer && (type === 'image' || type === 'banner' || type === 'activity')) {
        _observer.observe(el);
      }

      newCount++;
      _log('[Tracker] 发现元素: ' + el.getAttribute('data-track-id') + ' (type=' + type + ')');
    }

    if (newCount > 0) {
      _log('[Tracker] 本次扫描新增 ' + newCount + ' 个元素');
    }
    return els.length;
  }

  function setupClickTracking() {
    document.addEventListener('click', function (e) {
      // 最近匹配原则：从点击目标向上找最近的 data-track-id
      // 业务容器上放 data-track-id，子元素（img/a/span）点击都能命中
      var el = e.target.closest('[data-track-id]');
      if (!el) return;
      trackClick(el);
    }, true);
    _log('[Tracker] click 事件委托已注册');
  }

  function setupMutationObserver() {
    if (!window.MutationObserver) return;

    // 防抖：短时间内多次 DOM 变化合并为一次 scan
    var pending = false;
    var mo = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        scanElements();
        pending = false;
      });
    });

    // 等 body 存在后再 observe
    var tryObserve = function () {
      if (document.body) {
        mo.observe(document.body, { childList: true, subtree: true });
        _log('[Tracker] MutationObserver 已启动');
      } else {
        setTimeout(tryObserve, 100);
      }
    };
    tryObserve();
  }

  function setupUnloadHandler() {
    window.addEventListener('beforeunload', function () {
      clearTimeout(_flushTimer);
      _flushTimer = null;
      if (_eventQueue.length) {
        var payload = JSON.stringify({ site: _config.site, events: _eventQueue });
        _eventQueue = [];
        if (navigator.sendBeacon) {
          var blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon(_config.endpoint, blob);
        }
      }
    });

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') flush();
    });
  }

  // ══════════════════════════════════════════════════════════════
  //  日志
  // ══════════════════════════════════════════════════════════════

  function _log(msg) {
    // 始终输出到控制台，方便排查
    console.log(msg);
  }

  function _debugLog(type, msg) {
    // 控制台也打印一份
    if (_config.debug) {
      console.log('%c[Tracker ' + type + '] %c' + msg,
        'color:#fff;background:' + (type === 'VIEW' ? '#67c23a' : '#409eff') + ';padding:1px 4px;border-radius:2px',
        'color:inherit');
    }
    if (!_config.debug && !_debugEl) return;
    if (!_debugEl) _createDebugPanel();

    var line = document.createElement('div');
    line.style.cssText = 'padding:2px 4px;font-size:11px;color:#e2e8f0;font-family:monospace';
    line.innerHTML = '<span style="color:#' + (type === 'VIEW' ? '67c23a' : '409eff') + '">' + type + '</span> ' + msg;
    _debugEl.appendChild(line);

    while (_debugEl.children.length > 20) _debugEl.removeChild(_debugEl.firstChild);

    clearTimeout(_debugTimer);
    _debugEl.style.opacity = '1';
    _debugTimer = setTimeout(function () { _debugEl.style.opacity = '0.5'; }, 3000);
  }

  function _createDebugPanel() {
    _debugEl = document.createElement('div');
    _debugEl.id = '__ht_debug';
    _debugEl.style.cssText =
      'position:fixed;bottom:12px;right:12px;z-index:99999;' +
      'background:rgba(15,23,42,0.92);border-radius:8px;padding:8px 0;' +
      'min-width:260px;max-width:360px;max-height:320px;overflow-y:auto;' +
      'opacity:0.5;transition:opacity 0.2s;pointer-events:none;' +
      'font-family:monospace;font-size:11px;line-height:1.4';
    _debugEl.innerHTML = '<div style="color:#94a3b8;padding:0 8px 4px;border-bottom:1px solid rgba(255,255,255,0.1);margin-bottom:4px">🔍 Tracker Debug</div>';
    document.body.appendChild(_debugEl);
  }

  // ══════════════════════════════════════════════════════════════
  //  初始化
  // ══════════════════════════════════════════════════════════════

  function init() {
    if (_isInitialized) return;

    // Shopify 主题编辑器模式 — 不采集，避免污染统计数据
    if (window.__ht_design ||
        (window.Shopify && window.Shopify.designMode) ||
        /preview_theme_id=/.test(location.search)) {
      _log('[Tracker] 检测到编辑器模式，跳过初始化');
      return;
    }

    _isInitialized = true;
    _log('[Tracker] 开始初始化, endpoint=' + _config.endpoint);

    fetchDeviceModel();

    setupViewTracking();
    setupClickTracking();
    setupMutationObserver();
    setupUnloadHandler();

    // 延迟重新扫描，捕获 Shopify Web Component 异步渲染的元素
    [1000, 3000, 6000].forEach(function (delay) {
      setTimeout(function () {
        var total = scanElements();
        _log('[Tracker] 延迟' + (delay / 1000) + 's 重新扫描完成，共发现 ' + total + ' 个带 data-track-id 的元素');
      }, delay);
    });

    _log('[Tracker] 初始化完成');
  }

  // ══════════════════════════════════════════════════════════════
  //  公开 API
  // ══════════════════════════════════════════════════════════════

  window.Tracker = {
    config: function (opts) {
      if (opts.endpoint !== undefined) _config.endpoint = opts.endpoint;
      if (opts.flushInterval !== undefined) _config.flushInterval = opts.flushInterval;
      if (opts.maxBatch !== undefined) _config.maxBatch = opts.maxBatch;
      if (opts.site !== undefined) _config.site = opts.site;
      if (opts.debug !== undefined) _config.debug = opts.debug;
      _log('[Tracker] 配置更新: endpoint=' + _config.endpoint + ', debug=' + _config.debug);
    },

    debug: function (on) {
      _config.debug = !!on;
      if (on && !_debugEl && document.body) _createDebugPanel();
      if (!on && _debugEl) {
        _debugEl.remove();
        _debugEl = null;
      }
      _log('[Tracker] debug=' + _config.debug);
    },

    /** 手动扫描（SPA 页面切换后调用） */
    scan: function () {
      _sentViews = {}; // 重置曝光记录，新页面重新计数
      var total = scanElements();
      _log('[Tracker] 手动扫描完成，发现 ' + total + ' 个元素');
    },

    track: function (trackId, eventType, extraMeta) {
      var meta = extraMeta || {};
      var imageFilename = getShopifyImageFilename(meta.src || '');
      var ev = {
        event: eventType,
        trackId: trackId,
        contentKey: imageFilename
          ? hash(trackId + '|' + imageFilename)
          : hash((meta.src || '') + '|' + trackId),
        elementType: meta.type || 'custom',
        pathname: getCleanPathname(),
        title: meta.title || '',
        text: meta.text || '',
        href: meta.href || '',
        alt: meta.alt || '',
        timestamp: Math.floor(Date.now() / 1000),
        sessionId: getSessionId(),
        visitorId: getVisitorId(),
        device: detectDevice(),
        browser: detectBrowser(),
        os: detectOS(),
        deviceModel: detectDeviceModel(),
        deviceBrand: detectDeviceBrand(),
        country: '',
        extra: meta.extra || (location.search ? { query: location.search } : {})
      };
      enqueue(ev);
      _debugLog(eventType.toUpperCase(), trackId);
    },

    flush: flush
  };

  // ── 自动初始化 ──────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
